"use client"

import { useState, useRef, useEffect, useCallback } from "react"
import { Search, X, Loader2 } from "lucide-react"
import { HttpTypes } from "@medusajs/types"
import LocalizedClientLink from "@modules/common/components/localized-client-link"
import { getInlineSearchProducts } from "./inline-search-actions"

interface SearchResult {
  id: string
  handle: string
  title: string
  thumbnail: string | null
  price?: string
}

export default function InlineSearchBar() {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState("")
  const [results, setResults] = useState<SearchResult[]>([])
  const [loading, setLoading] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  // Close on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener("mousedown", handleClick)
    return () => document.removeEventListener("mousedown", handleClick)
  }, [])

  // Close on ESC
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false)
    }
    window.addEventListener("keydown", handleKey)
    return () => window.removeEventListener("keydown", handleKey)
  }, [])

  // Debounced search
  useEffect(() => {
    if (!query.trim() || query.length < 2) {
      setResults([])
      return
    }
    setLoading(true)
    const timer = setTimeout(async () => {
      try {
        const hits = await getInlineSearchProducts(query)
        setResults(hits)
      } catch {
        setResults([])
      } finally {
        setLoading(false)
      }
    }, 300)
    return () => clearTimeout(timer)
  }, [query])

  const handleOpen = () => {
    setOpen(true)
    setTimeout(() => inputRef.current?.focus(), 50)
  }

  const handleClose = () => {
    setOpen(false)
    setQuery("")
    setResults([])
  }

  if (!open) {
    return (
      <button
        onClick={handleOpen}
        aria-label="Search"
        className="hover:text-[var(--primary)] transition-colors"
      >
        <Search size={20} />
      </button>
    )
  }

  return (
    <div ref={containerRef} className="relative">
      {/* Inline search input */}
      <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-full px-3 py-1.5 shadow-md w-64 sm:w-80">
        <Search size={16} className="text-gray-400 shrink-0" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search products..."
          className="flex-1 text-sm outline-none text-gray-900 placeholder-gray-400 bg-transparent"
          autoComplete="off"
        />
        {loading ? (
          <Loader2 size={14} className="text-gray-400 animate-spin shrink-0" />
        ) : (
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors shrink-0"
            aria-label="Close search"
          >
            <X size={14} />
          </button>
        )}
      </div>

      {/* Dropdown results */}
      {(results.length > 0 || (query.length >= 2 && !loading)) && (
        <div className="absolute right-0 top-full mt-2 bg-white rounded-lg shadow-xl border border-gray-100 w-80 sm:w-96 max-h-96 overflow-y-auto z-50">
          {results.length === 0 ? (
            <div className="p-4 text-sm text-gray-500 text-center">
              No results for &ldquo;{query}&rdquo;
            </div>
          ) : (
            <>
              <div className="p-2 space-y-0.5">
                {results.slice(0, 8).map((product) => (
                  <LocalizedClientLink
                    key={product.id}
                    href={`/products/${product.handle}`}
                    className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 transition-colors group"
                    onClick={handleClose}
                  >
                    <div className="w-12 h-12 shrink-0 rounded overflow-hidden bg-gray-100">
                      {product.thumbnail ? (
                        // eslint-disable-next-line @next/next/no-img-element
                        <img
                          src={product.thumbnail}
                          alt={product.title}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full bg-gray-200" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 line-clamp-1 group-hover:text-[var(--primary)] transition-colors">
                        {product.title}
                      </p>
                      {product.price && (
                        <p className="text-xs text-gray-500 mt-0.5">{product.price}</p>
                      )}
                    </div>
                  </LocalizedClientLink>
                ))}
              </div>
              {results.length > 8 && (
                <div className="border-t border-gray-100 p-3 text-center">
                  <LocalizedClientLink
                    href={`/results/${encodeURIComponent(query)}`}
                    className="text-sm text-[var(--primary)] hover:underline font-medium"
                    onClick={handleClose}
                  >
                    See all {results.length} results →
                  </LocalizedClientLink>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  )
}
